import {GirafeHTMLElement} from '@geogirafe/lib-geoportal/base';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import CircleStyle from 'ol/style/Circle';

declare global {
  interface Window {
    sitnExterns?: Record<string, unknown>;
  }
}

/**
 * Displays, on demand, the parcels belonging to a farming exploitation ("SAU").
 */
class SitnFarmingParcelsComponent extends GirafeHTMLElement {
  override templateUrl = './template.html';
  override styleUrls = ['./style.css'];

  visible = false;
  tooltipMessage = '';

  private readonly vectorSource = new VectorSource();
  private readonly vectorLayer: VectorLayer<VectorSource>;

  constructor() {
    super('sitn-farming-parcels');
    this.vectorLayer = new VectorLayer({
      source: this.vectorSource,
      style: new Style({
        stroke: new Stroke({color: 'rgba(170, 0, 0, 1)', width: 2}),
        fill: new Fill({color: 'rgba(255, 255, 255, 0.4)'}),
        image: new CircleStyle({
          radius: 7,
          stroke: new Stroke({color: 'rgba(170, 0, 0, 1)', width: 2}),
          fill: new Fill({color: 'rgba(255, 255, 255, 0.4)'}),
        }),
      }),
      zIndex: 100,
    });
  }

  override connectedCallback() {
    super.connectedCallback();
    this.render();
    this.vectorLayer.setMap(this.context.mapManager.getMap());
    const externs = (window.sitnExterns ??= {});
    externs['farmingParcels'] = this.farmingParcels.bind(this);
  }

  override render() {
    super.render();
    if (this.visible) {
      this.show();
    } else {
      this.hide();
    }
  }

  get tooltipText(): string {
    return this.tooltipMessage;
  }

  /**
   * Entry point for the `sitnExterns.farmingParcels(...)`
   * link rendered by the agriculture MapServer template in the feature-info result.
   */
  private async farmingParcels(
    url: string,
    params: Record<string, string | number>,
    tooltipMessage: string,
  ): Promise<void> {
    this.tooltipMessage = tooltipMessage;
    try {
      const query = new URLSearchParams(
        Object.fromEntries(Object.entries(params).map(([key, value]) => [key, String(value)])),
      );
      const response = await fetch(`${url}?${query}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = (await response.json()) as object;
      this.vectorSource.clear();
      this.vectorSource.addFeatures(new GeoJSON().readFeatures(data));
      this.visible = true;
      this.render();
    } catch (err) {
      console.error('Farming parcels service error:', err);
      this.state.infobox.elements.push({
        id: `farming-parcels-${Math.random().toString(36).slice(2)}`,
        text: 'Une erreur est survenue. Merci de contacter le SITN (sitn@ne.ch).',
        type: 'error',
        duration: 5000,
      });
    }
  }

  reset(): void {
    this.vectorSource.clear();
    this.visible = false;
    this.render();
  }
}

export default SitnFarmingParcelsComponent;
